import { Link } from 'react-router-dom';
import { ExternalLink } from 'lucide-react';
import type { AgentHealth, SocAgent } from '@/api/soc';
import { Chip, type Tone } from '@/components/network/NetworkKit';
import { fmtAge } from '@/lib/rmm-helpers';
import { insightsDevicePath, ticketPath } from '@/lib/routes';
import { cn } from '@/lib/utils';

/** Shared bits of the SOC (Huntress) pages. */

export const SEVERITY_TONE: Record<string, Tone> = { critical: 'bad', high: 'bad', medium: 'warn', low: 'info' };

export function SeverityChip({ severity }: { severity: string | null }) {
  const s = (severity ?? '').toLowerCase();
  return <Chip tone={SEVERITY_TONE[s] ?? 'muted'}>{s ? s[0].toUpperCase() + s.slice(1) : 'Unknown'}</Chip>;
}

export function IncidentStatusChip({ status }: { status: string | null }) {
  return status === 'closed' ? <Chip tone="muted">Closed</Chip> : <Chip tone="warn">{status === 'sent' ? 'Open' : status ?? 'Open'}</Chip>;
}

export const HEALTH_LABEL: Record<AgentHealth, string> = {
  healthy: 'Healthy', defender: 'Defender unhealthy', 'no-edr': 'EDR not installed', silent: 'Not checking in', stale: 'Stale', isolated: 'Isolated',
};
const HEALTH_TONE: Record<AgentHealth, Tone> = { healthy: 'good', defender: 'warn', 'no-edr': 'warn', silent: 'bad', stale: 'muted', isolated: 'bad' };

export function HealthChip({ a }: { a: Pick<SocAgent, 'health' | 'defender' | 'defenderDetail' | 'lastCallback'> }) {
  const title = a.health === 'defender' ? `Defender: ${a.defender} · ${a.defenderDetail}` : a.health === 'silent' || a.health === 'stale' ? `Last callback ${fmtAge(a.lastCallback)} ago` : undefined;
  return <Chip tone={HEALTH_TONE[a.health]} title={title}>{HEALTH_LABEL[a.health]}</Chip>;
}

/** The Datto RMM device this agent matched (→ Device Orbit), or "not in Datto RMM". */
export function RmmMatchCell({ a }: { a: Pick<SocAgent, 'rmm'> }) {
  if (!a.rmm) return <span className="whitespace-nowrap text-xs italic text-muted-foreground">not in Datto RMM</span>;
  return (
    <Link to={insightsDevicePath(`rmm:${a.rmm.uid}`)} className="block max-w-[200px] text-xs text-foreground hover:underline" title={`Matched by ${a.rmm.matchedBy}. Opens Device Orbit.`}>
      <span className="block truncate">{a.rmm.hostname}</span>
      <span className={cn('block text-[11px]', a.rmm.online ? 'text-success' : 'text-muted-foreground')}>{a.rmm.online ? '● online' : '○ offline'}</span>
    </Link>
  );
}

export function TicketLinks({ ids }: { ids: number[] }) {
  if (!ids.length) return <span className="text-xs italic text-muted-foreground">no ticket</span>;
  return <span className="flex flex-wrap gap-1">{ids.map((id) => <Link key={id} to={ticketPath(id)} className="text-xs text-foreground hover:underline">#{id}</Link>)}</span>;
}

export function HuntressLink({ href, label = 'Open in Huntress' }: { href: string; label?: string }) {
  return <a href={href} target="_blank" rel="noreferrer" title={label} aria-label={label} className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground"><ExternalLink className="h-3.5 w-3.5" /></a>;
}

export const INDICATOR_LABEL: Record<string, string> = {
  process_detections: 'Process detection', footholds: 'Foothold', mde_detections: 'Defender detection', managed_identity: 'Identity (ITDR)',
  antivirus_detections: 'Antivirus', ransomware_canaries: 'Ransomware canary', monitored_files: 'Monitored files', behavioral_detections: 'Behavioural',
  siem_detections: 'SIEM', email_security_detections: 'Email security', app_control: 'App control', favicon_detections: 'Favicon', ai_misuse: 'AI misuse',
};
